import { useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { VStack } from '@cbhq/cds-web/layout';
import { TabLabel } from '@cbhq/cds-web/tabs';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { TextHeadline } from '@cbhq/cds-web/typography';

import { useIsProjectTeamsEnabled } from ':cloud/hooks/featureFlags/useIsProjectTeamsEnabled';
import { useIsWebhooksEnabled } from ':cloud/hooks/featureFlags/useIsWebhooksEnabled';
import { APIKeyRouter } from ':cloud/pages/APIKeyRouter';
import { BillingPage } from ':cloud/pages/Billing';
import { ProjectDashboard } from ':cloud/pages/ProjectDashboard';
import { TeamPage } from ':cloud/pages/TeamPage';
import { WebhooksPage } from ':cloud/pages/Webhooks';
import { useGetOrganization } from ':cloud/queries/OrganizationQueries/useGetOrganization';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { AppRoute } from ':cloud/utils/routes';
import { RoutedTab, RoutedTopNavTabs } from ':cloud/widgets/sharedcomponents/RoutedTopNavTabs';

export function Projects() {
  const isWebhooksEnabled = useIsWebhooksEnabled();
  const { activeOrg } = useGetUser();
  const orgId = activeOrg?.organizationId;
  const { orgHasMultipleUsers } = useGetOrganization(orgId);
  const isProjectTeamsEnabled = useIsProjectTeamsEnabled();

  const tabs = useMemo(() => {
    const baseTabs: RoutedTab[] = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        route: AppRoute.Projects.Overview,
      },
      {
        id: 'apiKeys',
        label: 'API Keys',
        route: AppRoute.Projects.APIKeys,
      },
      {
        id: 'billing',
        label: 'Billing',
        route: AppRoute.Projects.Billing,
      },
    ];

    if (orgHasMultipleUsers || isProjectTeamsEnabled) {
      baseTabs.push({
        id: 'team',
        label: 'Team',
        route: AppRoute.Projects.ProjectTeam,
      });
    }

    if (isWebhooksEnabled) {
      baseTabs.push({
        id: 'webhooks',
        label: (
          <TabLabel>
            <TextHeadline as="span" spacingEnd={1}>
              Webhooks
            </TextHeadline>
            <Tag colorScheme="yellow">Beta</Tag>
          </TabLabel>
        ),
        route: AppRoute.Projects.Webhooks,
      });
    }

    return baseTabs;
  }, [isWebhooksEnabled, orgHasMultipleUsers, isProjectTeamsEnabled]);

  return (
    <VStack width="100%">
      <RoutedTopNavTabs tabs={tabs} />
      <Switch>
        <Route exact path={AppRoute.Projects.Overview}>
          <ProjectDashboard />
        </Route>
        {isWebhooksEnabled && (
          <Route exact path={AppRoute.Projects.Webhooks}>
            <WebhooksPage />
          </Route>
        )}
        <Route path={AppRoute.Projects.APIKeys}>
          <APIKeyRouter />
        </Route>
        <Route exact path={AppRoute.Projects.Billing}>
          <BillingPage />
        </Route>
        {(orgHasMultipleUsers || isProjectTeamsEnabled) && (
          <Route exact path={AppRoute.Projects.ProjectTeam}>
            <TeamPage />
          </Route>
        )}
        <Redirect to={AppRoute.Projects.Overview} />
      </Switch>
    </VStack>
  );
}
